import React, { useState, useEffect, useCallback } from 'react';
import { chemistryApi } from '../../api/chemistry';

interface Reaction {
  id: number;
  file_id: number;
  rxn_index: number;
  reaction_smiles: string | null;
  file_name?: string;
}

interface LinkedReaction {
  id: number;
  reaction_id: number;
  role: string;
  reaction_smiles: string | null;
  file_id: number;
  file_name: string | null;
}

interface LinkEnzymeModalProps {
  isOpen: boolean;
  onClose: () => void;
  biologicId: number;
  biologicName: string | null;
  projectId?: number | null;
  onLinksUpdated?: () => void;
}

const ROLES = [
  { value: 'catalyst', label: 'Catalyst', description: 'Enzyme that catalyzes the reaction' },
  { value: 'substrate', label: 'Substrate', description: 'Input to the reaction' },
  { value: 'product', label: 'Product', description: 'Output of the reaction' },
  { value: 'inhibitor', label: 'Inhibitor', description: 'Inhibits the reaction' },
];

export const LinkEnzymeModal: React.FC<LinkEnzymeModalProps> = ({
  isOpen,
  onClose,
  biologicId,
  biologicName,
  projectId,
  onLinksUpdated,
}) => {
  const [reactions, setReactions] = useState<Reaction[]>([]);
  const [linkedReactions, setLinkedReactions] = useState<LinkedReaction[]>([]);
  const [selectedReactionIds, setSelectedReactionIds] = useState<number[]>([]);
  const [selectedRole, setSelectedRole] = useState('catalyst');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Load available reactions and existing links
  const loadData = useCallback(async () => {
    if (!isOpen) return;

    setLoading(true);
    try {
      // Load existing links
      const linksResponse = await chemistryApi.getBiologicReactions(biologicId);
      setLinkedReactions(linksResponse.reactions);

      // Load available reactions (from all reaction files)
      const reactionsResponse = await chemistryApi.getReactions({
        limit: 500,
      });

      // Add file names to reactions
      const reactionsWithFiles = reactionsResponse.reactions.map((r: any) => ({
        ...r,
        file_name: r.file_name || `File ${r.file_id}`,
      }));

      setReactions(reactionsWithFiles);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  }, [isOpen, biologicId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleToggleReaction = (reactionId: number) => {
    setSelectedReactionIds(prev =>
      prev.includes(reactionId)
        ? prev.filter(id => id !== reactionId)
        : [...prev, reactionId]
    );
  };

  const handleLink = async () => {
    if (selectedReactionIds.length === 0) return;

    setSaving(true);
    try {
      await chemistryApi.linkBiologicToReactions(biologicId, selectedReactionIds, selectedRole);
      setSelectedReactionIds([]);
      await loadData();
      onLinksUpdated?.();
    } catch (error) {
      console.error('Error linking reactions:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleUnlink = async (reactionId: number) => {
    try {
      await chemistryApi.unlinkBiologicFromReaction(biologicId, reactionId);
      await loadData();
      onLinksUpdated?.();
    } catch (error) {
      console.error('Error unlinking reaction:', error);
    }
  };

  // Filter reactions by search term and exclude already linked
  const linkedReactionIds = new Set(linkedReactions.map(lr => lr.reaction_id));
  const availableReactions = reactions.filter(r => {
    if (linkedReactionIds.has(r.id)) return false;
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      r.reaction_smiles?.toLowerCase().includes(term) ||
      r.file_name?.toLowerCase().includes(term) ||
      r.id.toString().includes(term)
    );
  });

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl w-[800px] max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <div>
            <h2 className="text-lg font-semibold">Link Enzyme to Reactions</h2>
            <p className="text-sm text-gray-500">{biologicName || `Biologic #${biologicId}`}</p>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-gray-500 hover:text-gray-700"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex">
          {/* Left: Available Reactions */}
          <div className="w-1/2 border-r flex flex-col">
            <div className="p-3 border-b bg-gray-50">
              <h3 className="font-medium text-sm mb-2">Available Reactions</h3>
              <input
                type="text"
                placeholder="Search reactions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-3 py-1.5 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-teal-500"
              />
            </div>
            <div className="flex-1 overflow-y-auto p-2">
              {loading ? (
                <div className="flex items-center justify-center h-32">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-teal-500"></div>
                </div>
              ) : availableReactions.length === 0 ? (
                <div className="text-center text-gray-500 py-8">
                  No reactions available
                </div>
              ) : (
                availableReactions.map((reaction) => (
                  <div
                    key={reaction.id}
                    onClick={() => handleToggleReaction(reaction.id)}
                    className={`p-2 mb-1 rounded cursor-pointer border ${
                      selectedReactionIds.includes(reaction.id)
                        ? 'border-teal-500 bg-teal-50'
                        : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Reaction #{reaction.id}</span>
                      <span className="text-xs text-gray-500">{reaction.file_name}</span>
                    </div>
                    {reaction.reaction_smiles && (
                      <p className="text-xs text-gray-600 font-mono mt-1 truncate">
                        {reaction.reaction_smiles}
                      </p>
                    )}
                  </div>
                ))
              )}
            </div>
            {/* Link controls */}
            <div className="p-3 border-t bg-gray-50">
              <div className="flex items-center gap-2 mb-2">
                <label className="text-sm text-gray-600">Role:</label>
                <select
                  value={selectedRole}
                  onChange={(e) => setSelectedRole(e.target.value)}
                  className="flex-1 px-2 py-1 text-sm border rounded"
                >
                  {ROLES.map((role) => (
                    <option key={role.value} value={role.value}>
                      {role.label}
                    </option>
                  ))}
                </select>
              </div>
              <button
                onClick={handleLink}
                disabled={selectedReactionIds.length === 0 || saving}
                className="w-full px-3 py-2 text-sm bg-teal-600 text-white rounded hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? 'Linking...' : `Link ${selectedReactionIds.length} Reaction${selectedReactionIds.length !== 1 ? 's' : ''}`}
              </button>
            </div>
          </div>

          {/* Right: Linked Reactions */}
          <div className="w-1/2 flex flex-col">
            <div className="p-3 border-b bg-gray-50">
              <h3 className="font-medium text-sm">Linked Reactions ({linkedReactions.length})</h3>
            </div>
            <div className="flex-1 overflow-y-auto p-2">
              {linkedReactions.length === 0 ? (
                <div className="text-center text-gray-500 py-8">
                  No linked reactions
                </div>
              ) : (
                linkedReactions.map((link) => (
                  <div
                    key={link.id}
                    className="p-2 mb-1 rounded border border-gray-200 flex items-center justify-between"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">Reaction #{link.reaction_id}</span>
                        <span className={`px-1.5 py-0.5 text-xs rounded ${
                          link.role === 'catalyst' ? 'bg-green-100 text-green-800' :
                          link.role === 'inhibitor' ? 'bg-red-100 text-red-800' :
                          link.role === 'substrate' ? 'bg-blue-100 text-blue-800' :
                          'bg-purple-100 text-purple-800'
                        }`}>
                          {link.role}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500">{link.file_name}</p>
                      {link.reaction_smiles && (
                        <p className="text-xs text-gray-600 font-mono truncate">
                          {link.reaction_smiles}
                        </p>
                      )}
                    </div>
                    <button
                      onClick={() => handleUnlink(link.reaction_id)}
                      className="ml-2 p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                      title="Remove link"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t bg-gray-50 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default LinkEnzymeModal;
